import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useContractorAuthStore } from '../../lib/contractorAuth';
import type { ContractorAuthResponse } from '../../lib/contractorAuth';
import { API_BASE } from '../../lib/apiBase';

const schema = z.object({
  email:    z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormData = z.infer<typeof schema>;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid #d1d5db',
  fontSize: 16,
  outline: 'none',
  boxSizing: 'border-box',
};

export function ContractorLogin() {
  const navigate  = useNavigate();
  const setAuth   = useContractorAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    try {
      const res = await fetch(`${API_BASE}/contractor/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const err = await res.json() as { error?: { message: string } };
        setError('root', { message: err.error?.message ?? 'Login failed.' });
        return;
      }

      const body = await res.json() as ContractorAuthResponse;
      setAuth(body.contractor, body.access_token, body.expires_in);
      navigate('/app/contractor/dashboard', { replace: true });
    } catch {
      setError('root', { message: 'Network error. Please try again.' });
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: '#f9fafb',
    }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: '#0f172a', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 28,
          }}>
            🔧
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            Contractor portal
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4, marginBottom: 0 }}>
            Sign in to manage your leads and account
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Email
            </label>
            <input
              {...register('email')}
              type="email"
              autoComplete="email"
              placeholder="you@business.com"
              style={inputStyle}
            />
            {errors.email && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.email.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Password
            </label>
            <input
              {...register('password')}
              type="password"
              autoComplete="current-password"
              style={inputStyle}
            />
            {errors.password && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.password.message}</p>
            )}
          </div>

          {errors.root && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, padding: '10px 14px',
              color: '#dc2626', fontSize: 14, textAlign: 'center',
            }}>
              {errors.root.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%', padding: '14px',
              background: isSubmitting ? '#4b5563' : '#0f172a',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#6b7280' }}>
          No account yet?{' '}
          <Link to="/contractor/signup" style={{ color: '#0f172a', fontWeight: 600, textDecoration: 'none' }}>
            Apply to join
          </Link>
        </p>
      </div>
    </div>
  );
}
